import EventDispatcher from "../../../../../shared/lib/html5-software-library/src/ranj/util/EventDispatcher";
import { getURLParameter } from "../../../../../shared/lib/html5-software-library/src/ranj/util/URLUtil";
import PlatformAPIEventType from "../../../../../shared/src/ranj/equalture/platform/PlatformAPIEventType";
import ProgressValue from "../../../../../shared/src/ranj/equalture/platform/ProgressValue";
import SaveEvent from "../../../../../shared/src/ranj/equalture/savegame/SaveEvent";
import LevelEndType from "../data/LevelEndType";
import GameConfig from "./GameConfig";
import GameStatus from "./GameStatus";
import PuzzleModel from "./PuzzleModel";
import TrackingController from "./TrackingController";

export default class GameModel extends EventDispatcher {

    /**
     * @param {GameConfig} gameConfig 
     * @param {TrackingController} trackingController 
     */

    constructor(dispatcher, gameConfig, trackingController, platformAPIService) {
        super();
        this._contextDispatcher = dispatcher;
        this._config = gameConfig;
        this._trackingController = trackingController;
        this._platformAPIService = platformAPIService;

        const speedParam = parseFloat(getURLParameter("speed"));
        this._speedfactor = isNaN(speedParam) ? 1 : speedParam;
        this.lastPuzzleSuccessful = false;
        this.tutorialAlreadySeen = false;
    }

    dispatchPlatformProgressEvent(progressValue) {
        this._trackingController.trackProgress(progressValue, this.elapsedTime);
    }

    initialize() {
        this.reset();
    }

    reset() {
        this._puzzleIndex = -1;
        this._sequenceIndex = 0;
        this._elapsedTime = 0;
    }

    set restoredFeedbackPending(bool) {
        this._restoredFeedbackPending = bool;
    }
    get restoredFeedbackPending() {
        return this._restoredFeedbackPending;
    }

    set tutorialAlreadySeen(bool) {
        this._tutorialAlreadySeen = bool;
    }

    get tutorialAlreadySeen() {
        return this._tutorialAlreadySeen;
    }

    get speedFactor() {
        return this._speedfactor;
    }

    startPractice() {
        //Overall time starts with practice
        this.elapsedTime = 0;
        //Start with first practice level
        this.status = GameStatus.PRACTICE;
        this._puzzleIndex = -1;
        this.doNextPuzzle();
    }

    start() {
        //Start with first level
        this.dispatchPlatformProgressEvent(ProgressValue.STARTED);
        this.status = GameStatus.RUNNING;
        this._puzzleIndex = -1;
        this.doNextPuzzle();
    }
    resumeAfterRestore() {
        //At this point, GameModel.restore must have been called already.
        this.dispatchPlatformProgressEvent(ProgressValue.STARTED);
        this.initPuzzle(this._puzzleIndex, this._sequenceIndex);
    }

    

    end() {
        this.dispatchPlatformProgressEvent(ProgressValue.COMPLETED);
        this.status = GameStatus.END;
    }

    getSaveData() {
        let status = this.status;
        let index = this.puzzleIndex;
        let sequenceIndex = this.sequenceIndex;
        //If puzzle was submitted, make sure we restore in next puzzle by saving next puzzle index / correct status
        //  NB: but not for practice puzzles. At the moment of implementing this, there was no time to 
        //  correctly handle all cases around practice, revisting tutorial, gatekeeper etc
        //We do this when saving instead of when restoring, because it is easer to handle it here,
        //  because this.restore is called from GameScreenMediator, 
        //  which should not even be instantiated in case last puzzle was submitted (and end screen should be shown)
        if (this.status == GameStatus.RUNNING && this._puzzleSubmitted) {
            //If puzzle can be retried, save next sequence index
            if (!this.lastPuzzleSuccessful) {
                if (this.canRetryPuzzle()) {
                    sequenceIndex++;
                } else if (!this.isPuzzleAllowFail()) {
                    //Game over (endscreen)
                    status = GameStatus.END;
                } else if (this.hasNextPuzzle()) {
                    //Allowed to fail, no retry available, not last puzzle: save index of next puzzle
                    index++;
                    sequenceIndex = 0;
                }
            } else if (this.hasNextPuzzle()) {
                //submitted puzzle successful, not last puzzle: save index of next puzzle
                index++;
                sequenceIndex = 0;
            } else {
                //No retries, no next puzzle: game over (endscreen)
                status = GameStatus.END;
            }
        }
        return {
            elapsed: Math.round(this.elapsedTime),
            status,
            index,
            lastPuzzleSuccessful: this.lastPuzzleSuccessful,
            sequenceIndex
        };
    }

    restore(obj) {
        //Restore puzzle
        this.status = obj.status;
        this.elapsedTime = obj.elapsed;
        this.lastPuzzleSuccessful = obj.lastPuzzleSuccessful;
        //Game is saved after the tutorial, so mark tutorial as seen:
        this._tutorialAlreadySeen = true;
        this._restoredFeedbackPending = true;

        this._puzzleIndex = obj.index;
        this._sequenceIndex = obj.sequenceIndex;
    }

    step(dt) {
        this.elapsedTime += dt;
        if (this._timerRunning) {
            if (this.remainingTime <= 0) {
                this.timeout();
            } else {
                if (this._elapsedTime - this._lastTimerUpdate > this._config.getValue("timings.delayRefreshTimer")) {
                    this.updateTimer();
                }
            }
        }
    }

    timeout() {
        this._timerRunning = false;
        this.dispatchEvent(GameModel.TIMEOUT);
    }

    updateTimer() {
        const p = this.remainingTime / this._availableTime;
        this._lastTimerUpdate = this._elapsedTime;
        this.dispatchEvent(GameModel.PERCENT_TIMER, p);
    }

    stopTimer() {
        this._timerRunning = false;
    }

    startTimer() {
        this._timerRunning = true;
        this._attemptStartTime = this.elapsedTime;
        const timerValue = this._puzzleModel.time;
        this._availableTime = timerValue * 1000;
        this.updateTimer();

        // PoC Async Events
        this._contextDispatcher.dispatch(PlatformAPIEventType.POC_ASYNC_EVENT);
    }

    set elapsedTime(value) {
        this._elapsedTime = value;
        this.dispatchEvent(GameModel.ELAPSED_TIME, this._elapsedTime);
    }
    get elapsedTime() {
        return this._elapsedTime;
    }
    get remainingTime() {
        return this._availableTime - (this._elapsedTime - this._attemptStartTime);
    }

    set status(value) {
        this._status = value;
        this.dispatchEvent(GameModel.STATUS, value);
    }
    get status() {
        return this._status;
    }

    hasNextPracticePuzzle() {
        return this._puzzleIndex < this._config.practicePuzzles.length - 1;
    }

    hasNextPuzzle() {
        return this._puzzleIndex < this._config.puzzles.length - 1;
    }

    isPuzzleAllowFail() {
        //Why this check?? This should never occur, right?
        if (this._puzzleModel === undefined) {
            return true;
        }
        return this._puzzleModel.allowFail;
    }

    retryPuzzle() {
        if (this.canRetryPuzzle()) {
            this.initPuzzle(this._puzzleIndex, this._sequenceIndex + 1);
            return true;
        }
        return false;
    }

    canRetryPuzzle() {
        return !this._puzzleModel.allowFail && this._puzzleModel.hasNextSequence;
    }

    doNextPuzzle() {
        this.initPuzzle(this._puzzleIndex + 1, 0);
    }

    isPreviousPondDifferent() {
        if (this.status != GameStatus.PRACTICE && this.hasNextPuzzle() && this._puzzleIndex > 0 && this._config.getPuzzle(this._puzzleIndex).location != this._config.getPuzzle(this._puzzleIndex - 1).location) {
            return true;
        }
        return false;
    }

    /**
     * 
     * @param {Number} index 
     * @param {Object} puzzleState - generic object containing savegame data for puzzle
     */
    initPuzzle(index, sequenceIndex = 0) {
        this._destroyPuzzle();

        const nbrPuzzles = this.status == GameStatus.PRACTICE ? this._config.practicePuzzles.length : this._config.puzzles.length;
        // Looping back to first puzzle when end is reached
        this._puzzleIndex = (index >= nbrPuzzles ? 0 : index);
        this._sequenceIndex = sequenceIndex;
        this._puzzleSubmitted = false;
        const puzzleCfg = this.status == GameStatus.PRACTICE ? this._config.getPracticePuzzle(index) : this._config.getPuzzle(index);
        this._puzzleModel = this._createPuzzleModel(puzzleCfg, sequenceIndex);
        this.lastPuzzleSuccessful = false;
        this._addPuzzleEventListeners();
        this.dispatchEvent(GameModel.PUZZLE, this._puzzleModel);
    }

    _createPuzzleModel(puzzleCfg, sequenceIndex = 0) {
        const ruleId = puzzleCfg.rule.id[sequenceIndex];
        const sequence = puzzleCfg.sequence[sequenceIndex];
        const hasNextSequence = sequenceIndex < puzzleCfg.sequence.length - 1;
        const correctInput = puzzleCfg.rule.correctInput[sequenceIndex];
        const ordered = puzzleCfg.rule.correctOrderRequired;
        return new PuzzleModel(
            puzzleCfg.location,
            puzzleCfg.allowFail,
            puzzleCfg.successFeedback,
            puzzleCfg.time,
            ruleId,
            sequence,
            ordered,
            hasNextSequence,
            correctInput
        );
    }

    _destroyPuzzle() {
        if (this._puzzleModel) {
            this._puzzleModel.removeEventListener(PuzzleModel.MOVE, this._handlePuzzleMove);
            this._puzzleModel = null;
        }
    }

    _addPuzzleEventListeners() {
        this._puzzleModel.addEventListener(PuzzleModel.MOVE, this._handlePuzzleMove);
    }

    selectItem(spot) {
        this.puzzle.pickSpot(spot);

        if (this._shouldTrack()) {
            this._trackingController.trackSpotSelected(this.puzzleNumber, spot.id, this.elapsedTime, this.attempt);
        }
    }

    deselectItem(spot) {
        this.puzzle.unpickSpot(spot);
        if (this._shouldTrack()) {
            this._trackingController.trackSpotDeselected(this.puzzleNumber, spot.id, this.elapsedTime, this.attempt);
        }
    }

    startPuzzleInteraction() {
        if (this._shouldTrack()) {
            this._trackingController.trackLevelStart(this.puzzleNumber, this.ruleId, this.elapsedTime, this.attempt);
        }
    }
    submitPuzzle(timeout = false) {
        const isSuccessful = this._puzzleModel.validate() && !timeout;
        this.lastPuzzleSuccessful = isSuccessful;
        this._puzzleSubmitted = true;

        if (this._shouldTrack()) {
            const submittedSequence = this._puzzleModel.pickedSpots.map(s => s.id);
            const correctSequence = this._puzzleModel.correctInput;
            const submittedBy = (timeout ? LevelEndType.TIMEOUT : LevelEndType.USER);
            this._trackingController.trackLevelSubmitted(
                this.puzzleNumber,
                this.ruleId,
                isSuccessful,
                submittedSequence,
                correctSequence,
                this.elapsedTime,
                this.attempt,
                submittedBy
            );
            this.dispatchEvent(GameModel.SUBMIT);
        }
        this._contextDispatcher.dispatch(SaveEvent.SAVE);
        return isSuccessful;
    }

    wasLastPuzzleSuccessful() {
        return this.lastPuzzleSuccessful;
    }

    get ruleId() {
        let ruleId = this._puzzleModel.ruleId;
        if (ruleId.indexOf("locatePattern") == 0) {
            ruleId = "locatePattern";
        }
        return ruleId;
    }

    get attempt() {
        return this._sequenceIndex + 1;
    }

    get sequenceIndex() {
        return this._sequenceIndex;
    }

    get puzzle() {
        return this._puzzleModel;
    }

    get puzzleIndex() {
        return this._puzzleIndex;
    }

    get puzzleNumber() {
        return this._puzzleIndex + 1;
    }

    _shouldTrack() {
        return this.status != GameStatus.TUTORIAL && this.status != GameStatus.PRACTICE;
    }

};


GameModel.STATUS = "GameModel.STATUS";
GameModel.PUZZLE = "GameModel.PUZZLE";
GameModel.SUBMIT = "GameModel.SUBMIT";
GameModel.ELAPSED_TIME = "GameModel.TIME";
GameModel.PERCENT_TIMER = "GameModel.PERCENT_TIMER";
GameModel.TIMEOUT = "GameModel.TIMEOUT";

GameModel.TUTORIAL_SEEN = "GameModel.TUTORIAL_SEEN";
GameModel.CLOSE_TUTORIAL_POPUP = "GameModel.CLOSE_TUTORIAL_POPUP";